import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryPublic } from "@/lib/rtk-query-config";
import { getBookByAbbrev, getBookDisplayName } from "@/lib/bible-books";
import type {
  BibleChapter,
  BibleChapterLayout,
  BibleCrossrefsResult,
  BiblePericope,
  BibleVersionCode,
} from "@/lib/bible-types";
import { RESOURCES } from "./resources";

type AbibliaChapterResponse = {
  book?: {
    abbrev?: { pt?: string; en?: string };
    name?: string;
  };
  chapter?: {
    number?: number;
    verses?: number;
  };
  verses?: Array<{
    number: number;
    text: string;
  }>;
  pericopes?: BiblePericope[];
  crossrefsCount?: Record<string, number>;
  layout?: BibleChapterLayout;
};

export type GetBibleChapterArgs = {
  version: BibleVersionCode;
  bookAbbrev: string;
  chapter: number;
};

export type GetBibleCrossrefsArgs = {
  bookAbbrev: string;
  chapter: number;
  verse: number;
  version: BibleVersionCode;
  loginId?: number;
};

function mapChapterResponse(
  args: GetBibleChapterArgs,
  data: AbibliaChapterResponse,
): BibleChapter {
  const book = args.bookAbbrev.toLowerCase();
  const catalogBook = getBookByAbbrev(book);

  return {
    version: args.version,
    bookAbbrev: book,
    bookName: catalogBook ? getBookDisplayName(catalogBook.abbrev) : (data.book?.name ?? book),
    chapter: data.chapter?.number ?? args.chapter,
    verses: (data.verses ?? []).map((item) => ({
      verse: item.number,
      text: item.text,
    })),
    pericopes: data.pericopes ?? [],
    crossrefsCount: data.crossrefsCount ?? {},
    ...(data.layout ? { layout: data.layout } : {}),
  };
}

export const bibliaApi = createApi({
  reducerPath: "bibliaAPI",
  baseQuery: baseQueryPublic,
  tagTypes: [RESOURCES.BIBLIA],
  endpoints: (build) => ({
    getChapter: build.query<BibleChapter, GetBibleChapterArgs>({
      query: ({ version, bookAbbrev, chapter }) => ({
        url: `/biblia/chapter/${version}/${bookAbbrev.toLowerCase()}/${chapter}`,
        method: "GET",
      }),
      transformResponse: (response: AbibliaChapterResponse, _meta, arg) =>
        mapChapterResponse(arg, response),
      providesTags: (_result, _error, arg) => [
        {
          type: RESOURCES.BIBLIA,
          id: `${arg.version}-${arg.bookAbbrev}-${arg.chapter}`,
        },
      ],
    }),
    getCrossrefs: build.query<BibleCrossrefsResult, GetBibleCrossrefsArgs>({
      query: ({ bookAbbrev, chapter, verse, version, loginId }) => ({
        url: `/biblia/crossrefs/${bookAbbrev.toLowerCase()}/${chapter}/${verse}`,
        method: "GET",
        params: {
          version,
          ...(loginId ? { loginId } : {}),
        },
      }),
      // Dados estáticos: mantém em cache por bastante tempo enquanto a aba estiver aberta.
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const { useGetChapterQuery, useLazyGetChapterQuery, useGetCrossrefsQuery } = bibliaApi;
